import { createRouter } from "@tanstack/solid-router";
import { NotFoundRoute } from "@/components/not-found-route";
import { rootRoute } from "@/routes/__root";
import { authRoute } from "@/routes/_auth/route";
import { loginRoute } from "@/routes/_auth/login";
import { registerRoute } from "@/routes/_auth/register";
import { forgotPasswordRoute } from "@/routes/_auth/forgot-password";
import { resetPasswordRoute } from "@/routes/_auth/reset-password";
import { verifyEmailRoute } from "@/routes/_auth/verify-email";
import { socialCallbackRoute } from "@/routes/_auth/social-callback";
import { pluginRoutes } from "@/composition";
import { appRoute } from "@/routes/_app/route";
import { indexRoute } from "@/routes/_app/index";
import { profileRoute } from "@/routes/_app/profile";
import { debugRoute } from "@/routes/_app/debug";
import { notesIndexRoute, notesRoute } from "@/routes/_app/notes";
import { notePageRoute } from "@/routes/_app/notes.$pageId";
import { sheetsIndexRoute, sheetsRoute } from "@/routes/_app/sheets";
import { sheetsDocRoute } from "@/routes/_app/sheets.$pageId";
import { slidesIndexRoute, slidesRoute } from "@/routes/_app/slides";
import { slidesDocRoute } from "@/routes/_app/slides.$pageId";
import { pdfRoute } from "@/routes/_app/pdf";
import { statsRoute } from "@/routes/_app/stats";
import { organizationsRoute } from "@/routes/_app/organizations";
import { organizationDetailRoute } from "@/routes/_app/organizations.$orgId";
import { invitationsRoute } from "@/routes/_app/invitations";
import { themeGalleryRoute } from "@/routes/_app/themes";
import { languageRoute } from "@/routes/_app/language";
import { profileFontsRoute } from "@/routes/_app/profile.fonts";
import { profileShortcutsRoute } from "@/routes/_app/profile.shortcuts";
import { profileDeleteAccountRoute } from "@/routes/_app/profile.delete-account";
import { profileTransferSendRoute } from "@/routes/_app/profile.transfer-send";
import { profileTransferReceiveRoute } from "@/routes/_app/profile.transfer-receive";
import { trashRoute } from "@/routes/_app/trash";

const routeTree = rootRoute.addChildren([
  socialCallbackRoute,
  authRoute.addChildren([
    loginRoute,
    registerRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    verifyEmailRoute,
  ]),
  appRoute.addChildren([
    indexRoute, // path: "/" — Dashboard
    ...pluginRoutes,
    notesRoute.addChildren([notesIndexRoute, notePageRoute]),
    sheetsRoute.addChildren([sheetsIndexRoute, sheetsDocRoute]),
    slidesRoute.addChildren([slidesIndexRoute, slidesDocRoute]),
    pdfRoute,
    statsRoute,
    organizationsRoute,
    organizationDetailRoute,
    invitationsRoute,
    themeGalleryRoute,
    languageRoute,
    profileRoute,
    profileFontsRoute,
    trashRoute,
    profileShortcutsRoute,
    profileDeleteAccountRoute,
    profileTransferSendRoute,
    profileTransferReceiveRoute,
    debugRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // "intent" preloads only when the user expresses intent (hover/focus on a
  // <Link>). Hover-only is the standard tradeoff: a tiny first-hover latency in
  // exchange for a calm post-login render.
  defaultPreload: "intent",
  // oxlint-disable-next-line eslint(no-magic-numbers)
  defaultPreloadStaleTime: 30_000, // 30 s — don't re-preload the same route within 30 s
  // Any unknown URL shows a clear "not found" state with an action
  // back to home, instead of a blank screen.
  defaultNotFoundComponent: NotFoundRoute,
});

declare module "@tanstack/solid-router" {
  interface Register {
    router: typeof router;
  }
}
